// Code generated by tygo. DO NOT EDIT.

//////////
// source: errors.go
/*
Package dto defines API request/response types and error handling.

This package contains all types used for HTTP API communication via goty:
  - Request types with path/query/json struct tags for parameter binding
  - Response types with string IDs and RFC3339 timestamps for JSON serialization
  - Structured error types with HTTP status codes and error codes
  - API-specific types (Property, UserRole, NodeType, Settings, etc.)

The dto package is the API contract layer, fully self-contained with no
dependency on the entity package. This ensures that changes to internal
domain models (entity) do not accidentally affect the API contract.

Conversion between dto and entity types is handled by the handlers package
(in convert.go), which imports both packages.

Error handling follows a structured pattern:
  - ErrorCode provides machine-readable error classification
  - APIError wraps errors with HTTP status codes and details
  - Constructor functions (NotFound, BadRequest, etc.) create common errors
*/

/**
 * ErrorCode defines specific error types for the API.
 */
/**
 * ErrorCodeValidationFailed is returned when input data fails validation.
 */
export const ErrorCodeValidationFailed = "VALIDATION_FAILED";
/**
 * ErrorCodeMissingField is returned when a required field is missing.
 */
export const ErrorCodeMissingField = "MISSING_FIELD";
/**
 * ErrorCodeInvalidFormat is returned when a field has an invalid format.
 */
export const ErrorCodeInvalidFormat = "INVALID_FORMAT";
/**
 * ErrorCodeNotFound is returned when a resource is not found.
 */
export const ErrorCodeNotFound = "NOT_FOUND";
/**
 * ErrorCodeNodeNotFound is returned when a node is not found.
 */
export const ErrorCodeNodeNotFound = "NODE_NOT_FOUND";
/**
 * ErrorCodeTableNotFound is returned when a table is not found.
 */
export const ErrorCodeTableNotFound = "TABLE_NOT_FOUND";
/**
 * ErrorCodeFileNotFound is returned when a file is not found.
 */
export const ErrorCodeFileNotFound = "FILE_NOT_FOUND";
/**
 * ErrorCodeStorageError is returned when a storage operation fails.
 */
export const ErrorCodeStorageError = "STORAGE_ERROR";
/**
 * ErrorCodeInternal is returned when an unexpected server error occurs.
 */
export const ErrorCodeInternal = "INTERNAL_ERROR";
/**
 * ErrorCodeNotImplemented is returned when a feature is not implemented.
 */
export const ErrorCodeNotImplemented = "NOT_IMPLEMENTED";
/**
 * ErrorCodeConflict is returned when there is a resource conflict.
 */
export const ErrorCodeConflict = "CONFLICT";
/**
 * ErrorCodeUnauthorized is returned when authentication is missing or invalid.
 */
export const ErrorCodeUnauthorized = "UNAUTHORIZED";
/**
 * ErrorCodeForbidden is returned when a user has insufficient permissions.
 */
export const ErrorCodeForbidden = "FORBIDDEN";
/**
 * ErrorCodeInvalidProvider is returned when an OAuth provider is unknown.
 */
export const ErrorCodeInvalidProvider = "INVALID_PROVIDER";
/**
 * ErrorCodeOAuthError is returned when an OAuth operation fails.
 */
export const ErrorCodeOAuthError = "OAUTH_ERROR";
/**
 * ErrorCodeExpired is returned when a resource has expired.
 */
export const ErrorCodeExpired = "EXPIRED";
/**
 * ErrorCodeRateLimitExceeded is returned when rate limit is exceeded.
 */
export const ErrorCodeRateLimitExceeded = "RATE_LIMIT_EXCEEDED";
/**
 * ErrorCodeCannotUnlinkOnlyAuth is returned when trying to unlink the only authentication method.
 */
export const ErrorCodeCannotUnlinkOnlyAuth = "CANNOT_UNLINK_ONLY_AUTH";
/**
 * ErrorCodeProviderAlreadyLinked is returned when trying to link an already linked provider.
 */
export const ErrorCodeProviderAlreadyLinked = "PROVIDER_ALREADY_LINKED";
/**
 * ErrorCodeProviderNotLinked is returned when trying to unlink a provider that is not linked.
 */
export const ErrorCodeProviderNotLinked = "PROVIDER_NOT_LINKED";
/**
 * ErrorCodeEmailInUse is returned when an email is already in use by another account.
 */
export const ErrorCodeEmailInUse = "EMAIL_IN_USE";
/**
 * ErrorCodeQuotaExceeded is returned when a server-wide quota is exceeded.
 */
export const ErrorCodeQuotaExceeded = "QUOTA_EXCEEDED";
/**
 * ErrorCodePayloadTooLarge is returned when the request body exceeds the size limit.
 */
export const ErrorCodePayloadTooLarge = "PAYLOAD_TOO_LARGE";
export type ErrorCode = typeof ErrorCodeValidationFailed | typeof ErrorCodeMissingField | typeof ErrorCodeInvalidFormat | typeof ErrorCodeNotFound | typeof ErrorCodeNodeNotFound | typeof ErrorCodeTableNotFound | typeof ErrorCodeFileNotFound | typeof ErrorCodeStorageError | typeof ErrorCodeInternal | typeof ErrorCodeNotImplemented | typeof ErrorCodeConflict | typeof ErrorCodeUnauthorized | typeof ErrorCodeForbidden | typeof ErrorCodeInvalidProvider | typeof ErrorCodeOAuthError | typeof ErrorCodeExpired | typeof ErrorCodeRateLimitExceeded | typeof ErrorCodeCannotUnlinkOnlyAuth | typeof ErrorCodeProviderAlreadyLinked | typeof ErrorCodeProviderNotLinked | typeof ErrorCodeEmailInUse | typeof ErrorCodeQuotaExceeded | typeof ErrorCodePayloadTooLarge;
/**
 * ErrorDetails defines the structured error information in a response.
 */
export interface ErrorDetails {
  code: ErrorCode;
  message: string;
}
/**
 * ErrorResponse is the standard API error response.
 */
export interface ErrorResponse {
  error: ErrorDetails;
  details?: { [key: string]: any};
}
/**
 * ErrorWithStatus is an error that includes an HTTP status code and error code.
 */
export type ErrorWithStatus = any;
/**
 * APIError is a concrete error type with status code and optional details.
 */
export interface APIError {
}

//////////
// source: request.go

/**
 * LoginRequest is a request to log in.
 */
export interface LoginRequest {
  email: string;
  password: string;
}
/**
 * RegisterRequest is a request to register a new user.
 */
export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
}
/**
 * GetMeRequest is a request to get current user info.
 */
export interface GetMeRequest {
}
/**
 * CreatePageRequest is a request to create a page under a parent node.
 * The parent ID is in the path ({id}); use "0" for root.
 */
export interface CreatePageRequest {
  title: string;
  content?: string;
}
/**
 * GetPageRequest is a request to get a page's content.
 */
export interface GetPageRequest {
}
/**
 * UpdatePageRequest is a request to update a page's content.
 */
export interface UpdatePageRequest {
  title: string;
  content: string;
}
/**
 * UpdatePageFrontmatterRequest is a request to update a page's icon and cover image.
 * Pass an empty string to clear a field.
 */
export interface UpdatePageFrontmatterRequest {
  icon: string;
  cover: string;
}
/**
 * DeletePageRequest is a request to delete a page from a node.
 * This removes the index.md but keeps the node directory if table data exists.
 */
export interface DeletePageRequest {
}
/**
 * DeleteNodeRequest is a request to delete a node.
 */
export interface DeleteNodeRequest {
}
/**
 * ListNodeVersionsRequest is a request to list node version history.
 */
export interface ListNodeVersionsRequest {
  Limit: number /* int */; // Max commits to return (1-1000, default 1000).
}
/**
 * GetNodeVersionRequest is a request to get a specific node version.
 */
export interface GetNodeVersionRequest {
}
/**
 * GetTableRequest is a request to get a table.
 * Now used for /nodes/{id}/table endpoint.
 */
export interface GetTableRequest {
}
/**
 * CreateTableRequest is a request to create a table under a parent node.
 * The parent ID is in the path ({id}); use "0" for root.
 */
export interface CreateTableRequest {
  title: string;
  properties: Property[];
}
/**
 * UpdateTableRequest is a request to update a table.
 * Now used for /nodes/{id}/table endpoint.
 */
export interface UpdateTableRequest {
  title: string;
  properties: Property[];
}
/**
 * DeleteTableRequest is a request to delete a table from a node.
 * This removes the metadata.json and data.jsonl but keeps the node directory if page exists.
 */
export interface DeleteTableRequest {
}
/**
 * CreateViewRequest is a request to create a new view for a table.
 */
export interface CreateViewRequest {
  name: string;
  type: ViewType;
}
/**
 * UpdateViewRequest is a request to update an existing view.
 */
export interface UpdateViewRequest {
  name?: string;
  type?: ViewType;
  columns?: ViewColumn[];
  filters?: Filter[];
  sorts?: Sort[];
  groups?: Group[];
}
/**
 * DeleteViewRequest is a request to delete a view.
 */
export interface DeleteViewRequest {
}
/**
 * ListRecordsRequest is a request to list records in a table.
 * Now used for /nodes/{id}/table/records endpoint.
 */
export interface ListRecordsRequest {
  ViewID: string; // Optional: apply saved view configuration
  Filters: string; // Optional: JSON-encoded ad-hoc filters
  Sorts: string; // Optional: JSON-encoded ad-hoc sorts
  Offset: number /* int */;
  Limit: number /* int */;
}
/**
 * CreateRecordRequest is a request to create a record.
 * Now used for /nodes/{id}/table/records/create endpoint.
 */
export interface CreateRecordRequest {
  data: { [key: string]: any};
}
/**
 * UpdateRecordRequest is a request to update a record.
 * Now used for /nodes/{id}/table/records/{rid} endpoint.
 */
export interface UpdateRecordRequest {
  data: { [key: string]: any};
}
/**
 * GetRecordRequest is a request to get a record.
 * Now used for /nodes/{id}/table/records/{rid} endpoint.
 */
export interface GetRecordRequest {
}
/**
 * DeleteRecordRequest is a request to delete a record.
 * Now used for /nodes/{id}/table/records/{rid}/delete endpoint.
 */
export interface DeleteRecordRequest {
}
/**
 * ListNodesRequest is a request to list nodes.
 */
export interface ListNodesRequest {
}
/**
 * GetNodeTitlesRequest is a request to get titles for multiple nodes.
 */
export interface GetNodeTitlesRequest {
  IDs: string; // Comma-separated node IDs
}
/**
 * GetNodeRequest is a request to get a node.
 */
export interface GetNodeRequest {
}
/**
 * ListNodeChildrenRequest is a request to list children of a node.
 */
export interface ListNodeChildrenRequest {
}
/**
 * MoveNodeRequest is a request to move a node to a new parent.
 */
export interface MoveNodeRequest {
  new_parent_id: string; // New parent node ID; 0 = root
}
/**
 * ListNodeAssetsRequest is a request to list assets in a node.
 */
export interface ListNodeAssetsRequest {
}
/**
 * UploadNodeAssetRequest is a request to upload an asset to a node.
 */
export interface UploadNodeAssetRequest {
}
/**
 * DeleteNodeAssetRequest is a request to delete an asset from a node.
 */
export interface DeleteNodeAssetRequest {
}
/**
 * ServeAssetRequest is a request to serve an asset file directly.
 */
export interface ServeAssetRequest {
}
/**
 * SearchRequest is a request to search pages and tables.
 */
export interface SearchRequest {
  query: string;
  limit?: number /* int */;
  match_title?: boolean;
  match_body?: boolean;
  match_fields?: boolean;
}
/**
 * CreateOrgInvitationRequest is a request to create an organization invitation.
 */
export interface CreateOrgInvitationRequest {
  email: string;
  role: OrganizationRole;
  locale?: string; // Optional: language for invitation email (en, fr, de, es)
}
/**
 * CreateWSInvitationRequest is a request to create a workspace invitation.
 */
export interface CreateWSInvitationRequest {
  email: string;
  role: WorkspaceRole;
  locale?: string; // Optional: language for invitation email (en, fr, de, es)
}
/**
 * ListOrgInvitationsRequest is a request to list invitations for an organization.
 */
export interface ListOrgInvitationsRequest {
}
/**
 * ListWSInvitationsRequest is a request to list invitations for a workspace.
 */
export interface ListWSInvitationsRequest {
}
/**
 * AcceptInvitationRequest is a request to accept an invitation.
 */
export interface AcceptInvitationRequest {
  token: string;
  password: string;
  name: string;
}
/**
 * SwitchWorkspaceRequest is a request to switch active workspace.
 */
export interface SwitchWorkspaceRequest {
  ws_id: string;
}
/**
 * UpdateWSMembershipSettingsRequest is a request to update user preferences within a workspace.
 */
export interface UpdateWSMembershipSettingsRequest {
  settings: WorkspaceMembershipSettings;
}
/**
 * UpdateOrgPreferencesRequest is a request to update organization-wide preferences.
 */
export interface UpdateOrgPreferencesRequest {
  settings?: OrganizationSettings;
  quotas?: OrganizationQuotas;
}
/**
 * GetOrganizationRequest is a request to get organization details.
 */
export interface GetOrganizationRequest {
}
/**
 * UpdateOrganizationRequest is a request to update organization details.
 */
export interface UpdateOrganizationRequest {
  name?: string;
}
/**
 * CreateOrganizationRequest is a request to create a new organization.
 */
export interface CreateOrganizationRequest {
  name: string;
}
/**
 * CreateWorkspaceRequest is a request to create a new workspace within an organization.
 */
export interface CreateWorkspaceRequest {
  name: string;
}
/**
 * GetWorkspaceRequest is a request to get workspace details.
 */
export interface GetWorkspaceRequest {
}
/**
 * UpdateWorkspaceRequest is a request to update workspace details.
 */
export interface UpdateWorkspaceRequest {
  name?: string;
  quotas?: WorkspaceQuotas;
  settings?: WorkspaceSettings;
}
/**
 * GetGitRemoteRequest is a request to get the git remote for a workspace.
 */
export interface GetGitRemoteRequest {
}
/**
 * UpdateGitRemoteRequest is a request to set (create or update) the git remote for a workspace.
 */
export interface UpdateGitRemoteRequest {
  url: string;
  type: string; // github, gitlab, custom
  auth_type: string; // token, ssh
  token?: string;
}
/**
 * DeleteGitRequest is a request to delete the git remote for a workspace.
 */
export interface DeleteGitRequest {
}
/**
 * PushGitRequest is a request to push to the git remote.
 */
export interface PushGitRequest {
}
/**
 * PullGitRequest is a request to pull from the git remote.
 */
export interface PullGitRequest {
}
/**
 * GetSyncStatusRequest is a request to get the sync status for a workspace.
 */
export interface GetSyncStatusRequest {
}
/**
 * SetupGitHubAppRemoteRequest is a request to configure a GitHub App-based remote.
 */
export interface SetupGitHubAppRemoteRequest {
  installation_id: number /* int64 */;
  repo_owner: string;
  repo_name: string;
  branch: string;
}
/**
 * ListGitHubAppReposRequest is a request to list repos for a GitHub App installation.
 */
export interface ListGitHubAppReposRequest {
  installation_id: number /* int64 */;
}
/**
 * ListGitHubAppInstallationsRequest is a request to list GitHub App installations.
 */
export interface ListGitHubAppInstallationsRequest {
}
/**
 * GitHubAppAvailableRequest is a request to check if GitHub App is configured.
 */
export interface GitHubAppAvailableRequest {
}
/**
 * ChangeEmailRequest is a request to change the user's email address.
 */
export interface ChangeEmailRequest {
  new_email: string;
  password: string; // Required for security verification
}
/**
 * SendVerificationEmailRequest is a request to send a verification email.
 */
export interface SendVerificationEmailRequest {
}
/**
 * VerifyEmailRequest is a request to verify an email via magic link token.
 */
export interface VerifyEmailRequest {
}
/**
 * LinkOAuthAccountRequest is a request to initiate linking an OAuth provider.
 */
export interface LinkOAuthAccountRequest {
  provider: OAuthProvider;
}
/**
 * UnlinkOAuthAccountRequest is a request to unlink an OAuth provider.
 */
export interface UnlinkOAuthAccountRequest {
  provider: OAuthProvider;
}
/**
 * SetPasswordRequest is a request to set or change the user's password.
 */
export interface SetPasswordRequest {
  current_password?: string; // Required if user has a password
  new_password: string;
}
/**
 * HealthRequest is a request to check system health.
 */
export interface HealthRequest {
}
/**
 * ProvidersRequest is a request to list configured OAuth providers.
 */
export interface ProvidersRequest {
}
/**
 * LogoutRequest is a request to logout (revoke current session).
 */
export interface LogoutRequest {
}
/**
 * ListSessionsRequest is a request to list user's active sessions.
 */
export interface ListSessionsRequest {
}
/**
 * RevokeSessionRequest is a request to revoke a specific session.
 */
export interface RevokeSessionRequest {
  session_id: string;
}
/**
 * RevokeAllSessionsRequest is a request to revoke all sessions (logout everywhere).
 */
export interface RevokeAllSessionsRequest {
}
/**
 * ListUsersRequest is a request to list users.
 */
export interface ListUsersRequest {
}
/**
 * UpdateOrgMemberRoleRequest is a request to update a user's organization role.
 */
export interface UpdateOrgMemberRoleRequest {
  user_id: string;
  role: OrganizationRole;
}
/**
 * RemoveOrgMemberRequest is a request to remove a user from an organization.
 */
export interface RemoveOrgMemberRequest {
  user_id: string;
}
/**
 * UpdateWSMemberRoleRequest is a request to update a user's workspace role.
 */
export interface UpdateWSMemberRoleRequest {
  user_id: string;
  role: WorkspaceRole;
}
/**
 * UpdateUserSettingsRequest is a request to update user global settings.
 */
export interface UpdateUserSettingsRequest {
  settings: UserSettings;
}
/**
 * AdminServerDetailRequest is a request to get admin dashboard data.
 */
export interface AdminServerDetailRequest {
}
/**
 * ServerConfigRequest is a request to get server configuration.
 */
export interface ServerConfigRequest {
}
/**
 * SMTPConfigUpdate contains SMTP configuration fields for updates.
 */
export interface SMTPConfigUpdate {
  host: string;
  port: number /* int32 */;
  username: string;
  password: string; // Empty preserves existing
  from: string;
}
/**
 * QuotasConfigUpdate contains quota configuration fields for updates.
 */
export interface QuotasConfigUpdate extends ResourceQuotas {
  max_request_body_bytes: number /* int64 */;
  max_sessions_per_user: number /* int */;
  max_organizations: number /* int */;
  max_workspaces: number /* int */;
  max_users: number /* int */;
  max_total_storage_bytes: number /* int64 */;
  max_egress_bandwidth_bps: number /* int64 */;
}
/**
 * RateLimitsConfigUpdate contains rate limit configuration fields for updates.
 */
export interface RateLimitsConfigUpdate {
  auth_rate_per_min: number /* int */;
  write_rate_per_min: number /* int */;
  read_auth_rate_per_min: number /* int */;
  read_unauth_rate_per_min: number /* int */;
}
/**
 * UpdateServerConfigRequest is a request to update server configuration.
 */
export interface UpdateServerConfigRequest {
  smtp?: SMTPConfigUpdate;
  quotas?: QuotasConfigUpdate;
  rate_limits?: RateLimitsConfigUpdate;
}
/**
 * NotionImportRequest is a request to start a Notion import into a new workspace.
 */
export interface NotionImportRequest {
  notion_token: string;
}
/**
 * NotionImportStatusRequest is a request to get the status of a Notion import.
 */
export interface NotionImportStatusRequest {
}
/**
 * NotionImportCancelRequest is a request to cancel a running Notion import.
 */
export interface NotionImportCancelRequest {
}
/**
 * ListNotificationsRequest is a request to list notifications for the authenticated user.
 */
export interface ListNotificationsRequest {
  Limit: number /* int */;
  Offset: number /* int */;
  UnreadOnly: boolean;
}
/**
 * GetUnreadCountRequest is a request to get the unread notification count.
 */
export interface GetUnreadCountRequest {
}
/**
 * MarkNotificationReadRequest marks a single notification as read.
 */
export interface MarkNotificationReadRequest {
}
/**
 * MarkAllNotificationsReadRequest marks all notifications as read.
 */
export interface MarkAllNotificationsReadRequest {
}
/**
 * DeleteNotificationRequest deletes a single notification.
 */
export interface DeleteNotificationRequest {
}
/**
 * GetNotificationPrefsRequest gets notification preferences.
 */
export interface GetNotificationPrefsRequest {
}
/**
 * UpdateNotificationPrefsRequest updates notification channel preferences.
 */
export interface UpdateNotificationPrefsRequest {
  overrides: { [key: string]: ChannelSetDTO};
}
/**
 * GetVAPIDKeyRequest gets the server's VAPID public key.
 */
export interface GetVAPIDKeyRequest {
}
/**
 * PushSubscribeRequest saves a push subscription.
 */
export interface PushSubscribeRequest {
  endpoint: string;
  p256dh: string;
  auth: string;
}
/**
 * PushUnsubscribeRequest removes a push subscription.
 */
export interface PushUnsubscribeRequest {
  endpoint: string;
}
/**
 * ListWorkspaceMembersRequest is a request to list workspace members.
 */
export interface ListWorkspaceMembersRequest {
}
/**
 * ResolveUsersRequest is a request to resolve user IDs to display info.
 */
export interface ResolveUsersRequest {
  ids: string[];
}

//////////
// source: response.go

/**
 * OkResponse is a simple success response.
 */
export interface OkResponse {
  ok: boolean;
}
/**
 * AuthResponse is a response containing authentication token and user info.
 * Used by login, register, and invitation acceptance endpoints.
 */
export interface AuthResponse {
  token: string;
  user?: UserResponse;
}
/**
 * ProvidersResponse is a response containing the list of configured OAuth providers.
 */
export interface ProvidersResponse {
  providers: OAuthProvider[];
}
/**
 * SessionResponse is the API representation of a session.
 */
export interface SessionResponse {
  id: string;
  device_info: string;
  ip_address: string;
  country_code?: string;
  created: Time;
  last_used: Time;
  is_current: boolean;
}
/**
 * ListSessionsResponse is a response containing user's sessions.
 */
export interface ListSessionsResponse {
  sessions: SessionResponse[];
}
/**
 * LogoutResponse is a response from logout.
 */
export type LogoutResponse = OkResponse;
/**
 * RevokeSessionResponse is a response from revoking a session.
 */
export type RevokeSessionResponse = OkResponse;
/**
 * RevokeAllSessionsResponse is a response from revoking all sessions.
 */
export interface RevokeAllSessionsResponse {
  revoked_count: number /* int */;
}
/**
 * UpdateNodeResponse is a response from updating a node.
 */
export interface UpdateNodeResponse {
  id: string;
}
/**
 * DeleteNodeResponse is a response from deleting a node.
 */
export type DeleteNodeResponse = OkResponse;
/**
 * ListNodeVersionsResponse is a response containing node version history.
 */
export interface ListNodeVersionsResponse {
  history: (Commit | undefined)[];
}
/**
 * GetNodeVersionResponse is a response containing node content at a version.
 */
export interface GetNodeVersionResponse {
  content: string;
}
/**
 * ListTablesResponse is a response containing a list of tables.
 */
export interface ListTablesResponse {
  tables: TableSummary[];
}
/**
 * TableSummary is a brief representation of a table for list responses.
 */
export interface TableSummary {
  id: string;
  title: string;
  created: Time;
  modified: Time;
}
/**
 * GetTableResponse is a response containing a table.
 */
export interface GetTableResponse {
  id: string;
  title: string;
  properties: Property[];
  created: Time;
  modified: Time;
}
/**
 * CreateTableResponse is a response from creating a table.
 */
export interface CreateTableResponse {
  id: string;
}
/**
 * UpdateTableResponse is a response from updating a table.
 */
export interface UpdateTableResponse {
  id: string;
}
/**
 * DeleteTableResponse is a response from deleting a table.
 */
export type DeleteTableResponse = OkResponse;
/**
 * CreateViewResponse is a response from creating a view.
 */
export interface CreateViewResponse {
  id: string;
}
/**
 * UpdateViewResponse is a response from updating a view.
 */
export interface UpdateViewResponse {
  id: string;
}
/**
 * DeleteViewResponse is a response from deleting a view.
 */
export type DeleteViewResponse = OkResponse;
/**
 * ListRecordsResponse is a response containing a list of records.
 */
export interface ListRecordsResponse {
  records: DataRecordResponse[];
}
/**
 * CreateRecordResponse is a response from creating a record.
 */
export interface CreateRecordResponse {
  id: string;
}
/**
 * UpdateRecordResponse is a response from updating a record.
 */
export interface UpdateRecordResponse {
  id: string;
}
/**
 * GetRecordResponse is a response containing a record.
 */
export interface GetRecordResponse {
  id: string;
  data: { [key: string]: any};
  created: Time;
  modified: Time;
}
/**
 * DeleteRecordResponse is a response from deleting a record.
 */
export type DeleteRecordResponse = OkResponse;
/**
 * ListNodesResponse is a response containing a list of nodes.
 */
export interface ListNodesResponse {
  nodes: NodeResponse[];
}
/**
 * MoveNodeResponse is a response from moving a node.
 */
export type MoveNodeResponse = OkResponse;
/**
 * ListNodeAssetsResponse is a response containing a list of assets.
 */
export interface ListNodeAssetsResponse {
  assets: AssetSummary[];
}
/**
 * AssetSummary is a brief representation of an asset for list responses.
 */
export interface AssetSummary {
  id: string;
  name: string;
  size: number /* int64 */;
  mime_type: string;
  created: Time;
  url: string;
}
/**
 * UploadNodeAssetResponse is a response from uploading an asset.
 */
export interface UploadNodeAssetResponse {
  id: string;
  name: string;
  size: number /* int64 */;
  mime_type: string;
  url: string;
}
/**
 * DeleteNodeAssetResponse is a response from deleting an asset.
 */
export type DeleteNodeAssetResponse = OkResponse;
/**
 * ServeAssetResponse wraps the binary asset data.
 */
export interface ServeAssetResponse {
  data: string;
  mime_type: string;
}
/**
 * SearchResponse is the response to a search request.
 */
export interface SearchResponse {
  results: SearchResult[];
}
/**
 * ListOrgInvitationsResponse is a response containing a list of organization invitations.
 */
export interface ListOrgInvitationsResponse {
  invitations: OrgInvitationResponse[];
}
/**
 * ListWSInvitationsResponse is a response containing a list of workspace invitations.
 */
export interface ListWSInvitationsResponse {
  invitations: WSInvitationResponse[];
}
/**
 * WorkspaceMemberResponse is a compact user representation for workspace member listings.
 * Used by the user column type to populate the picker and display names.
 */
export interface WorkspaceMemberResponse {
  id: string;
  name: string;
  email: string;
  avatar_url?: string;
}
/**
 * ListWorkspaceMembersResponse is a response containing current workspace members.
 */
export interface ListWorkspaceMembersResponse {
  members: WorkspaceMemberResponse[];
}
/**
 * ResolvedUser represents a resolved user reference from a user-type column value.
 * IsGhost is true when the user ID is valid but the user is no longer a member.
 */
export interface ResolvedUser {
  id: string;
  name: string;
  email: string;
  avatar_url?: string;
  is_ghost?: boolean;
}
/**
 * ResolveUsersResponse is a response containing resolved user details.
 */
export interface ResolveUsersResponse {
  users: ResolvedUser[];
}
/**
 * SwitchWorkspaceResponse is a response from switching workspace.
 */
export interface SwitchWorkspaceResponse {
  token: string;
  user?: UserResponse;
}
/**
 * ChangeEmailResponse is a response from changing email.
 */
export interface ChangeEmailResponse {
  ok: boolean;
  email_verified: boolean;
  message?: string;
}
/**
 * SendVerificationEmailResponse is a response from sending a verification email.
 */
export interface SendVerificationEmailResponse {
  ok: boolean;
  message?: string;
}
/**
 * LinkOAuthAccountResponse is a response from initiating OAuth linking.
 */
export interface LinkOAuthAccountResponse {
  redirect_url: string;
}
/**
 * UnlinkOAuthAccountResponse is a response from unlinking an OAuth provider.
 */
export type UnlinkOAuthAccountResponse = OkResponse;
/**
 * HealthResponse is a response from a health check.
 */
export interface HealthResponse {
  status: string;
  version: string;
  go_version: string;
  revision: string;
  dirty: boolean;
}
/**
 * ListUsersResponse is a response containing a list of users.
 */
export interface ListUsersResponse {
  users: UserResponse[];
}
/**
 * UserResponse is the API representation of a user.
 */
export interface UserResponse {
  id: string;
  email: string;
  email_verified?: boolean;
  name: string;
  is_global_admin?: boolean;
  has_password?: boolean;
  oauth_identities?: OAuthIdentity[];
  settings: UserSettings;
  created: Time;
  modified: Time;
  /**
   * Current context
   */
  organization_id?: string;
  org_role?: OrganizationRole;
  workspace_id?: string;
  workspace_name?: string;
  workspace_role?: WorkspaceRole;
  /**
   * All memberships
   */
  organizations?: OrgMembershipResponse[];
  workspaces?: WSMembershipResponse[];
}
/**
 * OrgMembershipResponse is the API representation of an organization membership.
 */
export interface OrgMembershipResponse {
  id: string;
  user_id: string;
  organization_id: string;
  organization_name?: string;
  role: OrganizationRole;
  created: Time;
}
/**
 * WSMembershipResponse is the API representation of a workspace membership.
 */
export interface WSMembershipResponse {
  id: string;
  user_id: string;
  workspace_id: string;
  workspace_name?: string;
  organization_id: string;
  role: WorkspaceRole;
  settings: WorkspaceMembershipSettings;
  created: Time;
}
/**
 * OrgInvitationResponse is the API representation of an organization invitation (excludes Token).
 */
export interface OrgInvitationResponse {
  id: string;
  email: string;
  organization_id: string;
  role: OrganizationRole;
  invited_by: string;
  expires_at: Time;
  created: Time;
}
/**
 * WSInvitationResponse is the API representation of a workspace invitation (excludes Token).
 */
export interface WSInvitationResponse {
  id: string;
  email: string;
  workspace_id: string;
  role: WorkspaceRole;
  invited_by: string;
  expires_at: Time;
  created: Time;
}
/**
 * OrganizationResponse is the API representation of an organization.
 */
export interface OrganizationResponse {
  id: string;
  name: string;
  billing_email?: string;
  quotas: OrganizationQuotas;
  settings: OrganizationSettings;
  member_count: number /* int */;
  workspace_count: number /* int */;
  created: Time;
  /**
   * ServerResourceLimits shows the server-imposed upper bound for each resource quota.
   * The organization's resource quotas cannot exceed these server-level limits.
   */
  server_resource_limits: ResourceQuotas;
}
/**
 * WorkspaceResponse is the API representation of a workspace.
 */
export interface WorkspaceResponse {
  id: string;
  organization_id: string;
  name: string;
  quotas: WorkspaceQuotas;
  settings: WorkspaceSettings;
  git_remote?: GitRemoteResponse;
  member_count: number /* int */;
  created: Time;
  /**
   * ParentResourceLimits shows the effective upper bound imposed by the server and organization.
   * The workspace's resource quotas cannot exceed these combined limits.
   * Zero means unlimited at the parent level.
   */
  parent_resource_limits: ResourceQuotas;
}
/**
 * GitRemoteResponse is the API representation of a git remote.
 */
export interface GitRemoteResponse {
  workspace_id: string;
  url: string;
  type: string;
  auth_type: string;
  installation_id?: number /* int64 */;
  repo_owner?: string;
  repo_name?: string;
  branch?: string;
  sync_status?: string;
  last_sync_error?: string;
  created: Time;
  last_sync?: Time;
}
/**
 * GitHubAppRepoResponse represents a GitHub repository from App installation.
 */
export interface GitHubAppRepoResponse {
  full_name: string;
  owner: string;
  name: string;
  private: boolean;
  html_url: string;
}
/**
 * ListGitHubAppReposResponse is a response containing GitHub App repos.
 */
export interface ListGitHubAppReposResponse {
  repos: GitHubAppRepoResponse[];
}
/**
 * GitSyncStatusResponse is a response containing sync status.
 */
export interface GitSyncStatusResponse {
  sync_status: string;
  last_sync?: Time;
  last_sync_error?: string;
}
/**
 * GitHubAppInstallationResponse represents a GitHub App installation.
 */
export interface GitHubAppInstallationResponse {
  id: number /* int64 */;
  account: string;
}
/**
 * ListGitHubAppInstallationsResponse is a response containing GitHub App installations.
 */
export interface ListGitHubAppInstallationsResponse {
  installations: GitHubAppInstallationResponse[];
}
/**
 * GitHubAppAvailableResponse indicates whether GitHub App is configured.
 */
export interface GitHubAppAvailableResponse {
  available: boolean;
}
/**
 * PullGitResponse is a response from pulling from git remote.
 */
export type PullGitResponse = OkResponse;
/**
 * NodeResponse is the API representation of a node.
 */
export interface NodeResponse {
  id: string;
  parent_id?: string;
  title: string;
  content?: string;
  properties?: Property[];
  views?: View[];
  created: Time;
  modified: Time;
  tags?: string[];
  favicon_url?: string;
  icon?: string;
  cover?: string;
  has_page: boolean;
  has_table: boolean;
  has_children?: boolean;
  children?: NodeResponse[];
  asset_urls?: { [key: string]: string};
  backlinks?: BacklinkInfo[];
}
/**
 * GetPageResponse is a response containing page content.
 */
export interface GetPageResponse {
  id: string;
  title: string;
  content: string;
  created: Time;
  modified: Time;
}
/**
 * CreatePageResponse is a response from creating a page.
 */
export interface CreatePageResponse {
  id: string;
}
/**
 * UpdatePageResponse is a response from updating a page.
 */
export interface UpdatePageResponse {
  id: string;
}
/**
 * UpdatePageFrontmatterResponse is a response from updating a page's icon and cover.
 */
export interface UpdatePageFrontmatterResponse {
  id: string;
}
/**
 * DeletePageResponse is a response from deleting a page.
 */
export type DeletePageResponse = OkResponse;
/**
 * GetTableSchemaResponse is a response containing table schema.
 */
export interface GetTableSchemaResponse {
  id: string;
  title: string;
  properties: Property[];
  views?: View[];
  created: Time;
  modified: Time;
}
/**
 * CreateTableUnderParentResponse is a response from creating a table under a parent.
 */
export interface CreateTableUnderParentResponse {
  id: string;
}
/**
 * ListNodeChildrenResponse is a response containing children of a node.
 */
export interface ListNodeChildrenResponse {
  nodes: NodeResponse[];
}
/**
 * GetNodeTitlesResponse is a response containing a map of node IDs to titles.
 */
export interface GetNodeTitlesResponse {
  titles: { [key: string]: string};
}
/**
 * BacklinkInfo represents a page that links to this page.
 */
export interface BacklinkInfo {
  node_id: string;
  title: string;
}
/**
 * DataRecordResponse is the API representation of a data record.
 */
export interface DataRecordResponse {
  id: string;
  data: { [key: string]: any};
  created: Time;
  modified: Time;
}
/**
 * AdminServerDetail contains server-wide dashboard data.
 */
export interface AdminServerDetail {
  user_count: number /* int */;
  org_count: number /* int */;
  workspace_count: number /* int */;
  total_storage: number /* int64 */;
  active_sessions: number /* int */;
  organizations: AdminOrgDetail[];
  request_metrics: AdminRequestMetrics;
}
/**
 * AdminOrgDetail contains organization details for the admin dashboard.
 */
export interface AdminOrgDetail {
  id: string;
  name: string;
  member_count: number /* int */;
  workspace_count: number /* int */;
  created: Time;
  workspaces: AdminWorkspaceDetail[];
}
/**
 * AdminWorkspaceDetail contains workspace details for the admin dashboard.
 */
export interface AdminWorkspaceDetail {
  id: string;
  org_id: string;
  name: string;
  member_count: number /* int */;
  page_count: number /* int */;
  storage_bytes: number /* int64 */;
  git_commits: number /* int */;
  created: Time;
}
/**
 * AdminRequestMetrics contains request throughput metrics.
 */
export interface AdminRequestMetrics {
  server_start_time: number /* float64 */;
  uptime_seconds: number /* float64 */;
  auth_count: number /* int64 */;
  write_count: number /* int64 */;
  read_auth_count: number /* int64 */;
  read_unauth_count: number /* int64 */;
}
/**
 * ListWorkspacesResponse is a response containing a list of workspaces.
 */
export interface ListWorkspacesResponse {
  workspaces: WorkspaceResponse[];
}
/**
 * NotionImportResponse is a response from starting a Notion import.
 */
export interface NotionImportResponse {
  workspace_id: string;
  workspace_name: string;
  status: string;
}
/**
 * NotionImportStatusResponse is a response containing import status.
 */
export interface NotionImportStatusResponse {
  status: string;
  progress: number /* int */;
  total: number /* int */;
  message?: string;
  pages?: number /* int */;
  databases?: number /* int */;
  records?: number /* int */;
  assets?: number /* int */;
  errors?: number /* int */;
  duration_ms?: number /* int64 */;
}
/**
 * NotionImportCancelResponse is a response from cancelling a Notion import.
 */
export type NotionImportCancelResponse = OkResponse;
/**
 * SMTPConfigResponse contains SMTP configuration for the response (password masked).
 */
export interface SMTPConfigResponse {
  host: string;
  port: number /* int32 */;
  username: string;
  from: string;
}
/**
 * QuotasConfigResponse contains quota configuration for the response.
 */
export interface QuotasConfigResponse extends ResourceQuotas {
  max_request_body_bytes: number /* int64 */;
  max_sessions_per_user: number /* int */;
  max_organizations: number /* int */;
  max_workspaces: number /* int */;
  max_users: number /* int */;
  max_total_storage_bytes: number /* int64 */;
  max_egress_bandwidth_bps: number /* int64 */;
}
/**
 * RateLimitsConfigResponse contains rate limit configuration for the response.
 */
export interface RateLimitsConfigResponse {
  auth_rate_per_min: number /* int */;
  write_rate_per_min: number /* int */;
  read_auth_rate_per_min: number /* int */;
  read_unauth_rate_per_min: number /* int */;
}
/**
 * ServerConfigResponse is a response containing server configuration.
 */
export interface ServerConfigResponse {
  smtp: SMTPConfigResponse;
  quotas: QuotasConfigResponse;
  rate_limits: RateLimitsConfigResponse;
}
/**
 * UpdateServerConfigResponse is a response from updating server configuration.
 */
export type UpdateServerConfigResponse = OkResponse;
/**
 * ListNotificationsResponse is a response containing paginated notifications.
 */
export interface ListNotificationsResponse {
  notifications: NotificationDTO[];
  total: number /* int */;
  unread_count: number /* int */;
}
/**
 * UnreadCountResponse returns the unread notification count.
 */
export interface UnreadCountResponse {
  count: number /* int */;
}
/**
 * VAPIDKeyResponse returns the server's VAPID public key.
 */
export interface VAPIDKeyResponse {
  public_key: string;
}
/**
 * MarkNotificationReadResponse is a response from marking a notification read.
 */
export type MarkNotificationReadResponse = OkResponse;
/**
 * MarkAllNotificationsReadResponse is a response from marking all notifications read.
 */
export type MarkAllNotificationsReadResponse = OkResponse;
/**
 * DeleteNotificationResponse is a response from deleting a notification.
 */
export type DeleteNotificationResponse = OkResponse;
/**
 * PushSubscribeResponse is a response from subscribing to push notifications.
 */
export type PushSubscribeResponse = OkResponse;
/**
 * PushUnsubscribeResponse is a response from unsubscribing from push notifications.
 */
export type PushUnsubscribeResponse = OkResponse;

//////////
// source: types.go

/**
 * Time is a type alias for storage.Time to ensure it generates as 'number' in TypeScript.
 */
export type Time = number;
/**
 * PropertyType represents the type of a table property.
 */
/**
 * PropertyTypeText stores plain text values.
 */
export const PropertyTypeText = "text";
/**
 * PropertyTypeNumber stores numeric values (integer or float).
 */
export const PropertyTypeNumber = "number";
/**
 * PropertyTypeCheckbox stores boolean values.
 */
export const PropertyTypeCheckbox = "checkbox";
/**
 * PropertyTypeDate stores ISO8601 date strings.
 */
export const PropertyTypeDate = "date";
/**
 * PropertyTypeSelect stores a single selection from predefined options.
 */
export const PropertyTypeSelect = "select";
/**
 * PropertyTypeMultiSelect stores multiple selections from predefined options.
 */
export const PropertyTypeMultiSelect = "multi_select";
/**
 * PropertyTypeUser stores a reference to a workspace member by user ID.
 */
export const PropertyTypeUser = "user";
/**
 * PropertyTypeURL stores URLs with validation.
 */
export const PropertyTypeURL = "url";
/**
 * PropertyTypeEmail stores email addresses with validation.
 */
export const PropertyTypeEmail = "email";
/**
 * PropertyTypePhone stores phone numbers with validation.
 */
export const PropertyTypePhone = "phone";
export type PropertyType = typeof PropertyTypeText | typeof PropertyTypeNumber | typeof PropertyTypeCheckbox | typeof PropertyTypeDate | typeof PropertyTypeSelect | typeof PropertyTypeMultiSelect | typeof PropertyTypeUser | typeof PropertyTypeURL | typeof PropertyTypeEmail | typeof PropertyTypePhone;
/**
 * SelectOption represents an option for select/multi_select properties.
 */
export interface SelectOption {
  id: string;
  name: string;
  color?: string;
}
/**
 * Property represents a table column with its configuration.
 */
export interface Property {
  name: string;
  type: PropertyType;
  required?: boolean;
  /**
   * Options contains the allowed values for select and multi_select properties.
   * Each option has an ID (used in storage), name (display), and optional color.
   */
  options?: SelectOption[];
}
/**
 * OrganizationRole defines the role of a user within an organization.
 */
export type OrganizationRole = string;
/**
 * OrgRoleOwner has full control including billing.
 */
export const OrgRoleOwner: OrganizationRole = "org:owner";
/**
 * OrgRoleAdmin can manage workspaces and members.
 */
export const OrgRoleAdmin: OrganizationRole = "org:admin";
/**
 * OrgRoleMember can only access granted workspaces.
 */
export const OrgRoleMember: OrganizationRole = "org:member";
/**
 * WorkspaceRole defines the permissions for a user within a workspace.
 */
export type WorkspaceRole = string;
/**
 * WSRoleAdmin has full workspace control.
 */
export const WSRoleAdmin: WorkspaceRole = "ws:admin";
/**
 * WSRoleEditor can create and modify content.
 */
export const WSRoleEditor: WorkspaceRole = "ws:editor";
/**
 * WSRoleViewer can only read content.
 */
export const WSRoleViewer: WorkspaceRole = "ws:viewer";
/**
 * UserSettings represents global user preferences.
 */
export interface UserSettings {
  theme: string;
  language: string;
  last_active_workspaces?: string[];
}
/**
 * OAuthProvider represents a supported OAuth2 provider.
 */
/**
 * OAuthProviderGoogle represents Google OAuth.
 */
export const OAuthProviderGoogle = "google";
/**
 * OAuthProviderMicrosoft represents Microsoft OAuth.
 */
export const OAuthProviderMicrosoft = "microsoft";
/**
 * OAuthProviderGitHub represents GitHub OAuth.
 */
export const OAuthProviderGitHub = "github";
export type OAuthProvider = typeof OAuthProviderGoogle | typeof OAuthProviderMicrosoft | typeof OAuthProviderGitHub;
/**
 * OAuthIdentity represents a link between a local user and an OAuth2 provider.
 */
export interface OAuthIdentity {
  provider: OAuthProvider;
  provider_id: string;
  email: string;
  avatar_url?: string;
  last_login: Time;
}
/**
 * WorkspaceMembershipSettings represents user preferences within a specific workspace.
 */
export interface WorkspaceMembershipSettings {
  notifications: boolean;
}
/**
 * ResourceQuotas defines per-workspace content limits shared by server, org, and workspace layers.
 * At org and workspace layers: -1 means "inherit from parent", 0 means "disabled", positive means a limit.
 * At server layer all fields must be strictly positive.
 */
export interface ResourceQuotas {
  max_pages: number /* int */;
  max_storage_bytes: number /* int64 */;
  max_records_per_table: number /* int */;
  max_asset_size_bytes: number /* int64 */;
  max_tables_per_workspace: number /* int */;
  max_columns_per_table: number /* int */;
}
/**
 * OrganizationQuotas defines limits for an organization.
 */
export interface OrganizationQuotas extends ResourceQuotas {
  max_workspaces_per_org: number /* int */;
  max_members_per_org: number /* int */;
  max_members_per_workspace: number /* int */;
  max_total_storage_bytes: number /* int64 */;
}
/**
 * WorkspaceQuotas is a type alias for ResourceQuotas.
 * -1 means "inherit from parent", 0 means "disabled", positive means a limit.
 */
export type WorkspaceQuotas = ResourceQuotas;
/**
 * UserQuota defines limits for a user.
 */
export interface UserQuota {
  max_organizations: number /* int */;
}
/**
 * OrganizationSettings represents organization-wide settings.
 */
export interface OrganizationSettings {
  default_workspace_quotas: WorkspaceQuotas;
}
/**
 * WorkspaceSettings represents workspace-wide settings.
 */
export interface WorkspaceSettings {
  allowed_domains?: string[];
  public_access: boolean;
  git_auto_push: boolean;
}
/**
 * Commit represents a commit in git history.
 */
export interface Commit {
  hash: string;
  message: string;
  author_name: string;
  author_email: string;
  timestamp: Time;
}
/**
 * SearchResult represents a single search result.
 */
export interface SearchResult {
  type: string; // "page" or "record"
  node_id: string;
  record_id?: string;
  title: string;
  snippet: string;
  score: number /* float64 */;
  matches: { [key: string]: string};
  modified: Time;
}
/**
 * View represents a saved table view configuration.
 */
export interface View {
  id: string;
  name: string;
  type: ViewType;
  default?: boolean;
  columns?: ViewColumn[];
  filters?: Filter[];
  sorts?: Sort[];
  groups?: Group[];
}
/**
 * ViewType defines the layout type for a view.
 */
/**
 * ViewTypeTable displays records in a spreadsheet-like table.
 */
export const ViewTypeTable = "table";
/**
 * ViewTypeBoard displays records in a kanban board grouped by a property.
 */
export const ViewTypeBoard = "board";
/**
 * ViewTypeGallery displays records as cards in a grid.
 */
export const ViewTypeGallery = "gallery";
/**
 * ViewTypeList displays records in a simple list.
 */
export const ViewTypeList = "list";
/**
 * ViewTypeCalendar displays records on a calendar by date property.
 */
export const ViewTypeCalendar = "calendar";
export type ViewType = typeof ViewTypeTable | typeof ViewTypeBoard | typeof ViewTypeGallery | typeof ViewTypeList | typeof ViewTypeCalendar;
/**
 * ViewColumn defines the visibility and width of a property column.
 */
export interface ViewColumn {
  property: string;
  width?: number /* int */;
  visible: boolean;
}
/**
 * Filter defines a condition for filtering records.
 */
export interface Filter {
  property?: string;
  operator?: FilterOp;
  value?: any;
  and?: Filter[];
  or?: Filter[];
}
/**
 * FilterOp defines the comparison operator for a filter.
 */
/**
 * FilterOpEquals matches if value equals the filter value.
 */
export const FilterOpEquals = "equals";
/**
 * FilterOpNotEquals matches if value does not equal the filter value.
 */
export const FilterOpNotEquals = "not_equals";
/**
 * FilterOpContains matches if value contains the filter value (text).
 */
export const FilterOpContains = "contains";
/**
 * FilterOpNotContains matches if value does not contain the filter value.
 */
export const FilterOpNotContains = "not_contains";
/**
 * FilterOpStartsWith matches if value starts with the filter value.
 */
export const FilterOpStartsWith = "starts_with";
/**
 * FilterOpEndsWith matches if value ends with the filter value.
 */
export const FilterOpEndsWith = "ends_with";
/**
 * FilterOpGreaterThan matches if value is greater than the filter value.
 */
export const FilterOpGreaterThan = "gt";
/**
 * FilterOpLessThan matches if value is less than the filter value.
 */
export const FilterOpLessThan = "lt";
/**
 * FilterOpGreaterEqual matches if value is greater than or equal to the filter value.
 */
export const FilterOpGreaterEqual = "gte";
/**
 * FilterOpLessEqual matches if value is less than or equal to the filter value.
 */
export const FilterOpLessEqual = "lte";
/**
 * FilterOpIsEmpty matches if value is empty/null.
 */
export const FilterOpIsEmpty = "is_empty";
/**
 * FilterOpIsNotEmpty matches if value is not empty/null.
 */
export const FilterOpIsNotEmpty = "is_not_empty";
export type FilterOp = typeof FilterOpEquals | typeof FilterOpNotEquals | typeof FilterOpContains | typeof FilterOpNotContains | typeof FilterOpStartsWith | typeof FilterOpEndsWith | typeof FilterOpGreaterThan | typeof FilterOpLessThan | typeof FilterOpGreaterEqual | typeof FilterOpLessEqual | typeof FilterOpIsEmpty | typeof FilterOpIsNotEmpty;
/**
 * Sort defines the sort order for a property.
 */
export interface Sort {
  property: string;
  direction: SortDir;
}
/**
 * SortDir defines the sort direction.
 */
export type SortDir = string;
/**
 * SortAsc sorts in ascending order (A-Z, 0-9, oldest-newest).
 */
export const SortAsc: SortDir = "asc";
/**
 * SortDesc sorts in descending order (Z-A, 9-0, newest-oldest).
 */
export const SortDesc: SortDir = "desc";
/**
 * Group defines how to group records by a property.
 */
export interface Group {
  property: string;
  hidden?: any[];
}
/**
 * EventType identifies the kind of workspace event sent via SSE.
 */
export type EventType = string;
/**
 * EventNodeCreated is sent when a new node (page or table) is created.
 */
export const EventNodeCreated: EventType = "node_created";
/**
 * EventNodeUpdated is sent when a node's content or title changes.
 */
export const EventNodeUpdated: EventType = "node_updated";
/**
 * EventNodeDeleted is sent when a node is removed.
 */
export const EventNodeDeleted: EventType = "node_deleted";
/**
 * EventNodeMoved is sent when a node is moved to a new parent.
 */
export const EventNodeMoved: EventType = "node_moved";
/**
 * EventRecordsChanged is sent when a table record is created, updated, or deleted.
 */
export const EventRecordsChanged: EventType = "records_changed";
/**
 * EventTableUpdated is sent when a table schema or view changes.
 */
export const EventTableUpdated: EventType = "table_updated";
/**
 * WorkspaceEvent is the payload sent to SSE clients for real-time workspace updates.
 */
export interface WorkspaceEvent {
  type: EventType;
  node_id: string;
  record_id?: string;
  actor_id: string;
  modified: Time;
}
/**
 * NotificationDTO is the API representation of a notification.
 */
export interface NotificationDTO {
  id: string;
  type: string;
  title: string;
  body?: string;
  resource_id?: string;
  actor_id?: string;
  actor_name?: string;
  read: boolean;
  created_at: Time;
}
/**
 * ChannelSetDTO indicates which delivery channels are enabled.
 */
export interface ChannelSetDTO {
  email: boolean;
  web: boolean;
}
/**
 * NotificationPrefsDTO holds user notification preferences.
 */
export interface NotificationPrefsDTO {
  defaults: { [key: string]: ChannelSetDTO};
  overrides: { [key: string]: ChannelSetDTO};
}
